<template>
    <div
        v-if="isVisible && shouldBeVisible"
        class="fixed inset-0 z-[60] cursor-pointer"
        style="clip-path: polygon(var(--safe-zone-polygon))"
        @mouseenter="handleMouseEnter"
        @mousemove="handleMouseMove"
        @mouseleave="hideSafeZone"
        @click="$emit('click')"
    >
        <div
            :class="twMerge(
                'absolute inset-0 bg-opacity-30',
                debug && 'bg-green-300 opacity-75',
            )"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'

type Position = {
    x: number
    y: number
    width: number
    height: number
}

type Props = {
    sourcePosition: Position
    targetPosition: Position
    debug?: boolean
}

const props = defineProps<Props>()

defineEmits<{
    click: []
}>()

const polygonPath = ref('')
const isVisible = ref(false)
const mouseStopTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const mouseInsideSafeZone = ref(false)

const computePolygonPath = () => {
    const source = props.sourcePosition
    const target = props.targetPosition

    // Calculate source button center point
    const sourceCenterX = source.x + source.width / 2
    const sourceCenterY = source.y + source.height / 2

    // Use the target position from parent
    const targetLeft = target.x
    const targetTop = target.y
    const targetBottom = target.y + target.height

    // Create a triangular safe zone from source to target
    const points = [
        // Start from source center
        `${sourceCenterX}px ${sourceCenterY}px`,
        // Top-left corner of target
        `${targetLeft}px ${targetTop}px`,
        // Bottom-left corner of target
        `${targetLeft}px ${targetBottom}px`,
    ]

    return points.join(', ')
}

const updatePolygonPath = () => {
    polygonPath.value = computePolygonPath()
}

// Update polygon when positions change
watch([() => props.sourcePosition, () => props.targetPosition], updatePolygonPath, { immediate: true, deep: true })
watch([() => props.sourcePosition], showSafeZone, {
    immediate: true, deep: true,
})

function clearMouseStopTimer() {
    if (mouseStopTimer.value) {
        clearTimeout(mouseStopTimer.value)
        mouseStopTimer.value = null
    }
}

function hideSafeZone() {
    clearMouseStopTimer()
    isVisible.value = false
    mouseInsideSafeZone.value = false
}

function handleMouseEnter() {
    mouseInsideSafeZone.value = true
    // Start watching for mouse stops when mouse enters safe zone
}

function handleMouseMove() {
    if (!mouseInsideSafeZone.value) {
        return
    }
    clearMouseStopTimer()
    mouseStopTimer.value = setTimeout(hideSafeZone, 50)
}

function showSafeZone() {
    isVisible.value = true
}

// Check if safe zone should be visible based on position logic
const shouldBeVisible = computed(() => {
    // Only show safe zone if source is to the left of the target
    // If source is already to the right, user doesn't need to cross other areas
    return props.sourcePosition.x < props.targetPosition.x
})

onMounted(() => {
    updatePolygonPath()

    // Set CSS custom property for the polygon
    const style = document.documentElement.style
    style.setProperty('--safe-zone-polygon', polygonPath.value)

    // Show immediately if position logic allows
    if (shouldBeVisible.value) {
        isVisible.value = true
    }
})

onUnmounted(() => {
    clearMouseStopTimer()
})

watch(polygonPath, (newPath) => {
    const style = document.documentElement.style
    style.setProperty('--safe-zone-polygon', newPath)
})
</script>
